import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CollectionsIcon from '@mui/icons-material/Collections';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Container,
    FormControlLabel,
    Grid,
    Paper,
    TextField,
    Typography
} from '@mui/material';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import AttachmentTaskType from '../../enum/AttachmentTaskType';
import CommentTaskType from '../../enum/CommentTaskType';
import FixedIds from '../../enum/FixedIds';
import CommentTask from '../../model/CommentTask';
import Task from '../../model/Task';
import TaskReminder from '../../model/TaskReminder';
import { useAuthentication } from '../../reducer/authentication';
import { ServiceApi } from '../../service/ServiceApiWithLoading';
import TaskApi from '../../service/TaskApi';
import { getUserDepartmentPermissions } from '../../utils';
import RouteIdParams from '../System/RouteIdParams';
import { ImagePreviewModal } from './ImagePreviewModal';
import AttachmentPreviewBox from './AttachmentPreviewBox';
import { getUploadUrl } from '../../service/ServiceApi';

const taskApi = new TaskApi();
const taskReminderApi = new ServiceApi<TaskReminder>('taskReminder');

export function TaskPageRoot() {
    const match = useRouteMatch<RouteIdParams>();
    const taskId = match.params.id;
    const [key, setKey] = useState(0);
    function resetKey() {
        setKey(prevKey => prevKey + 1);
    }
    return (
        <TaskPage key={`task-${taskId}-${key}`} resetKey={resetKey} taskId={taskId} />
    );
}

function CommentTaskBox({ comment, setFileOrUrl, setModalOpen }: { comment: CommentTask, setFileOrUrl: (file: File | string | null) => void, setModalOpen: (open: boolean) => void }) {
    const { user } = useAuthentication();
    const [commentTaskUser, setCommentTaskUser] = useState(comment.comment_task_users?.find(ctu => ctu.user_id === user?.id));

    async function updateCommentResolved(commentTaskUserId: number) {
        const updatedCommentTaskUser = await taskApi.resolveComment(commentTaskUserId);
        setCommentTaskUser(updatedCommentTaskUser);
    }

    async function updateCommentAwared(commentTaskUserId: number, alertThreeDays: boolean = false) {
        const updatedCommentTaskUser = await taskApi.awaredComment(commentTaskUserId);
        if (alertThreeDays) {
            const taskReminder: Partial<TaskReminder> = {
                comment: 'Lembrete',
                reminder_date: moment().add(3, 'days').format('YYYY-MM-DD HH:mm:00'),
                user_id: user?.id,
                user_ids: [user?.id || 0],
                task_id: comment.task_id
            };
            await taskReminderApi.insert(taskReminder);
        }
        setCommentTaskUser(updatedCommentTaskUser);
    }

    return <Box sx={{ backgroundColor: (theme) => theme.palette.background.default === '#202326' ? theme.palette.background.paper : '#111827', p: 2, borderRadius: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ color: (theme) => theme.palette.background.default === '#202326' ? theme.palette.text.secondary : 'rgb(148, 163, 184)', flex: 1 }}>
                {comment.user?.name} - {moment(comment.created_at).format('DD/MM/YYYY HH:mm')}
            </Typography>
            {comment.canceled ? <Chip label="Cancelado" size='small' variant="outlined" color='error' /> :
                commentTaskUser && commentTaskUser.resolved_at ? <Chip label="Resolvido" size='small' variant="outlined" color='success' /> :
                    commentTaskUser && commentTaskUser.awared_at ? <Chip label="Lido" size='small' variant="outlined" color='warning' /> : ''}
        </Box>
        <Typography variant="body2" mt={1} sx={{ wordWrap: 'break-word' }} dangerouslySetInnerHTML={{ __html: comment.comment || '' }} />
        {!comment.canceled && !!commentTaskUser ? <Box mt={1} display="flex" flexWrap="wrap" gap={2}>
            {!commentTaskUser.awared_at ? <>
                <Button color="warning" size='small' variant="contained" onClick={() => updateCommentAwared(commentTaskUser.id)}>Lido</Button>
                <Button color="warning" size='small' variant="contained" onClick={() => updateCommentAwared(commentTaskUser.id, true)}>Lido + alerta 3 dias</Button>
            </> : ''}
            {!commentTaskUser.resolved_at ? <Button color="success" size='small' variant="contained" onClick={() => updateCommentResolved(commentTaskUser.id)}>Resolvido</Button> : ''}
        </Box> : ''}
        {comment.attachments && comment.attachments.length > 0 && (
            <Grid container spacing={2} mt={2}>
                {comment.attachments.map((at, index) => (
                    <Grid item xs={6} md={6} key={index}>
                        <AttachmentPreviewBox file={at.file} id={at.task_id || null} model="Task" name={at.name} systemAttachmentId={at.system_attachment_id} systemAttachmentFile={at.system_attachment?.file} onClickImage={() => {
                            const url = (at.system_attachment_id && at.system_attachment?.file)
                                ? `${getUploadUrl()}SystemAttachment/${at.system_attachment_id}/${at.system_attachment.file}`
                                : `${getUploadUrl()}Task/${at.task_id}/${at.name}`;
                            setFileOrUrl(url);
                            setModalOpen(true);
                        }} />
                    </Grid>
                ))}
            </Grid>
        )}
    </Box>
}

function TaskPage({ taskId, resetKey }: { taskId: string, resetKey: () => void }) {
    const [comment, setComment] = useState('');
    const [relatedUsers, setRelatedUsers] = useState<{ name: string, id: number }[]>([]);
    const [withoutAttachment, setWithoutAttachment] = useState(false);
    const [withoutUser, setWithoutUser] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState<{ name: string, id: number }[]>([]);
    const [attachments, setAttachments] = useState<File[]>([]);
    const [task, setTask] = useState<Task | null>(null);
    const history = useHistory();
    const { user } = useAuthentication();
    const [fileOrUrl, setFileOrUrl] = useState<File | string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    function fillRelatedUsers(item: Task | null, isInternal: boolean) {
        let relatedUsersTemp: { name: string, id: number }[] = [
            { id: FixedIds.idDanielRaupp, name: 'Daniel Raupp' },
            { id: FixedIds.idHenrique, name: 'Henrique Macedo' },
            { id: FixedIds.idPedro, name: 'Pedro Lanzarini' },
            { id: FixedIds.idRenan, name: 'Renan Ribeiro' }
        ];
        if (item) {
            if (item.user && item.user.perm_task && relatedUsersTemp.findIndex(u => u.id === item.user?.id) === -1) {
                let permsByDpItemUser = getUserDepartmentPermissions(item.user);
                if (permsByDpItemUser.perm_menu_tasks || isInternal) {
                    relatedUsersTemp.push({ id: item.user.id, name: item.user.name });
                }
            }
            if (item.created_by_user && item.created_by_user.perm_task && relatedUsersTemp.findIndex(u => u.id === item.created_by_user_id) === -1) {
                let permsByDpCreatedBy = getUserDepartmentPermissions(item.created_by_user);
                if (permsByDpCreatedBy.perm_menu_tasks || isInternal) {
                    relatedUsersTemp.push({ id: item.created_by_user.id, name: item.created_by_user.name });
                }
            }
            item.comments?.forEach(comment => {
                if (comment.user?.name === 'LanzaBot') return;
                if (comment.user && comment.user.perm_task && relatedUsersTemp.findIndex(u => u.id === comment.user_id) === -1) {
                    let permsByDpCommentUser = getUserDepartmentPermissions(comment.user);
                    if (permsByDpCommentUser.perm_menu_tasks || isInternal) {
                        relatedUsersTemp.push({ id: comment.user.id, name: comment.user.name });
                    }
                }
                comment.comment_task_users?.forEach(commentTaskUser => {
                    if (commentTaskUser.user && commentTaskUser.user.perm_task && relatedUsersTemp.findIndex(u => u.id === commentTaskUser.user_id) === -1) {
                        let permsByDpCommentTaskUser = getUserDepartmentPermissions(commentTaskUser.user);
                        if (permsByDpCommentTaskUser.perm_menu_tasks || isInternal) {
                            relatedUsersTemp.push({ id: commentTaskUser.user.id, name: commentTaskUser.user.name });
                        }
                    }
                });
            });
        }
        setRelatedUsers(relatedUsersTemp.sort((a, b) => a.name.localeCompare(b.name)));
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const taskData = await taskApi.getById(taskId, { relations: ['user', 'comments', 'comments.user', 'comments.comment_task_users', 'ticket_status', 'created_by_user', 'comments.attachments', 'comments.attachments.system_attachment'] });
                setTask(taskData);
                fillRelatedUsers(taskData, FixedIds.finalCustomerCommboxId === taskData.final_customer_id);
            } catch (error) {
                console.error('Error fetching task data:', error);
            }
        };
        fetchData();
    }, [taskId]);

    async function saveComment(newComment: boolean) {
        if (!comment.trim()) {
            enqueueSnackbar('Comentário não pode estar vazio.', { variant: 'error' });
            return;
        }
        if (!withoutUser && selectedUsers.length === 0) {
            enqueueSnackbar('Você deve marcar pelo menos uma pessoa.', { variant: 'error' });
            return;
        }
        if (!withoutAttachment && attachments.length === 0) {
            enqueueSnackbar('Você deve adicionar pelo menos uma foto.', { variant: 'error' });
            return;
        }

        let savedComment = await taskApi.saveComment(taskId, comment, false, user?.id || 0, CommentTaskType.PreService, undefined, undefined, selectedUsers.map(u => u.id));
        if (savedComment) {
            await Promise.all(attachments.map(async (attachment) => {
                await taskApi.saveAttachment(attachment, taskId, '', AttachmentTaskType.Analysis, savedComment.id);
            }));
            await taskApi.sendCommentsEmail([savedComment.id]);
            enqueueSnackbar('Comentário salvo com sucesso!', { variant: 'success' });
            if (newComment) {
                resetKey();
            } else {
                history.replace(`/system`);
            }
        } else {
            enqueueSnackbar('Erro ao salvar comentário.', { variant: 'error' });
        }
    }

    if (!task) return null;

    const isInternal = FixedIds.finalCustomerCommboxId === task.final_customer_id;

    return (
        <>
            <Box sx={{ overflow: 'auto', height: '100%', backgroundColor: (theme) => theme.palette.background.default === '#202326' ? theme.palette.background.default : '#0a0e17' }}>
                <Box
                    sx={{
                        backgroundColor: (theme) => theme.palette.background.default === '#202326' ? theme.palette.background.paper : '#f3f4f6',
                        color: (theme) => theme.palette.background.default === '#202326' ? theme.palette.text.primary : '#111827',
                        px: 4,
                        py: 2,
                        display: 'flex',
                        alignItems: 'stretch',
                        borderBottom: (theme) => theme.palette.background.default === '#202326' ? `1px solid ${theme.palette.divider}` : '1px solid #ddd',
                        height: 65,
                        boxSizing: 'border-box'
                    }}
                >
                    <Box display="flex" alignItems="center" onClick={() => history.replace(`/system`)}>
                        <ArrowBackIcon sx={{ mr: 1 }} />
                    </Box>
                    <Box sx={{ ml: 4, flex: 1, overflow: 'hidden' }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                            {task.name} - {moment(task.created_at).format('DD/MM/YYYY')}
                        </Typography>
                        <Typography variant="body2">Status: {task.ticket_status?.name || '-'}</Typography>
                    </Box>
                </Box>
                <Container sx={{ pt: 4, pb: 4, color: (theme) => theme.palette.background.default === '#202326' ? theme.palette.text.primary : '#fff' }}>

                    {/* Tarefa Descrição */}
                    <Paper sx={{ backgroundColor: (theme) => theme.palette.background.default === '#202326' ? theme.palette.background.paper : '#1f2937', p: 2, mb: 3, borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ color: (theme) => theme.palette.background.default === '#202326' ? theme.palette.text.secondary : 'rgb(148, 163, 184)' }}>Descrição do chamado:</Typography>
                        <Typography variant="body2" mt={2} sx={{ wordWrap: 'break-word' }} dangerouslySetInnerHTML={{ __html: task.description || '' }} />
                    </Paper>

                    {/* Comentários */}
                    <Paper sx={{ backgroundColor: (theme) => theme.palette.background.default === '#202326' ? theme.palette.background.paper : '#1f2937', p: 2, mb: 3, borderRadius: 2 }}>
                        <Typography variant="subtitle1" mb={2} sx={{ color: (theme) => theme.palette.background.default === '#202326' ? theme.palette.text.secondary : 'rgb(148, 163, 184)' }}>Comentários atuais da tarefa:</Typography>

                        {(task.comments || [])?.length > 0 ? task.comments?.map((comment, index) => {
                            return <CommentTaskBox key={index} comment={comment} setFileOrUrl={setFileOrUrl} setModalOpen={setModalOpen} />;
                        }) : (
                            <Box sx={{ p: 2 }}>
                                <Typography variant="body2" sx={{ color: (theme) => theme.palette.background.default === '#202326' ? theme.palette.text.secondary : 'rgb(148, 163, 184)' }}>Nenhum comentário.</Typography>
                            </Box>
                        )}
                    </Paper>

                    {/* Novo comentário */}
                    <TextField
                        multiline
                        fullWidth
                        minRows={5}
                        placeholder="Digite seu comentário aqui..."
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        sx={{
                            backgroundColor: (theme) => theme.palette.background.default === '#202326' ? theme.palette.background.paper : '#1f2937',
                            borderRadius: 2,
                            mb: 3,
                            '& .MuiInputBase-root': { color: (theme) => theme.palette.background.default === '#202326' ? theme.palette.text.primary : '#fff', borderRadius: 2 },
                        }}
                    />

                    {/* Marcar pessoas e anexos */}
                    <Grid container spacing={0}>
                        <Grid item xs={12}>
                            <FormControlLabel control={<Checkbox checked={withoutAttachment} onChange={(e) => {
                                setWithoutAttachment(e.target.checked);
                                setAttachments([]);
                            }} />} label="Não vou adicionar foto" />
                        </Grid>
                    </Grid>
                    <Grid container spacing={0} mt={2}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Marcar pessoas:</Typography>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} mt={2}>
                        {relatedUsers.map(({ name, id }, idx) => (
                            <Grid item xs={6} key={id}>
                                <FormControlLabel control={<Checkbox value={id} disabled={withoutUser} sx={{ padding: '0 9px' }} checked={selectedUsers.some(user => user.id === id)} onChange={(e) => {
                                    if (e.target.checked) {
                                        setSelectedUsers([...selectedUsers, { name, id }]);
                                    } else {
                                        setSelectedUsers(selectedUsers.filter(user => user.id !== id));
                                    }
                                }} />} label={name} />
                            </Grid>
                        ))}
                    </Grid>
                    <Grid container spacing={0} mt={2}>
                        <Grid item xs={12}>
                            <FormControlLabel control={<Checkbox checked={withoutUser} onChange={(e) => {
                                setWithoutUser(e.target.checked);
                                setSelectedUsers([]);
                            }} />} label="Não vou marcar ninguém" />
                        </Grid>
                    </Grid>

                    {/* Botões de Galeria e Câmera */}
                    <Grid container spacing={3} mt={3}>
                        <Grid item xs={6} md={6}>
                            <Button
                                fullWidth
                                startIcon={<CollectionsIcon />}
                                sx={{ color: '#fff', padding: '20px' }}
                                size='large'
                                variant='outlined'
                                disabled={withoutAttachment}
                                onClick={() => {
                                    const input = document.createElement('input');
                                    input.type = 'file';
                                    input.accept = 'image/*';
                                    input.multiple = true;
                                    input.onchange = (e) => {
                                        const files = Array.from((e.target as HTMLInputElement).files || []);
                                        const imageFiles = files.filter(file => file.type.startsWith('image/'));
                                        setAttachments((o) => [...o, ...imageFiles]);
                                    };
                                    input.click();
                                }}
                            >
                                GALERIA
                            </Button>
                        </Grid>
                        <Grid item xs={6} md={6}>
                            <Button
                                fullWidth
                                startIcon={<PhotoCameraIcon />}
                                sx={{ color: '#fff', padding: '20px' }}
                                size='large'
                                variant='outlined'
                                disabled={withoutAttachment}
                                onClick={() => {
                                    //open smartphone camera
                                    const input = document.createElement('input');
                                    input.type = 'file';
                                    input.accept = 'image/*';
                                    input.capture = 'camera';
                                    input.onchange = (e) => {
                                        const files = Array.from((e.target as HTMLInputElement).files || []);
                                        const imageFiles = files.filter(file => file.type.startsWith('image/'));
                                        setAttachments((o) => [...o, ...imageFiles]);
                                    };
                                    input.click();
                                }}
                            >
                                CÂMERA
                            </Button>
                        </Grid>
                    </Grid>
                    {/* Lista as imagens anexadas */}
                    {attachments.length > 0 && (
                        <Grid container spacing={2} mt={2}>
                            {attachments.map((file, index) => (
                                <Grid item xs={6} md={6} key={index}>
                                    <AttachmentPreviewBox file={file} id={task.id || null} model="Task" name={file.name} onDelete={(e) => {
                                        e.stopPropagation();
                                        setAttachments((o) => o.filter((_, i) => i !== index))
                                    }} onClickImage={() => {
                                        setFileOrUrl(file);
                                        setModalOpen(true);
                                    }} />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                    {/* Botões de ação */}
                    <Grid container spacing={3} mt={3}>
                        <Grid item xs={6} md={6}>
                            <Button
                                fullWidth
                                variant="contained"
                                sx={{ backgroundColor: '#4b5563', padding: '20px' }}
                                size='large'
                                onClick={() => saveComment(false)}
                            >
                                Salvar
                            </Button>
                        </Grid>
                        <Grid item xs={6} md={6}>
                            <Button
                                fullWidth
                                variant="contained"
                                sx={{ backgroundColor: '#059669', padding: '20px' }}
                                size='large'
                                onClick={() => saveComment(true)}
                            >
                                Salvar e Novo
                            </Button>
                        </Grid>
                    </Grid>

                    {/* Voltar */}
                    <Grid container spacing={2} mt={1}>
                        <Grid item xs={12} md={12}>
                            <Button fullWidth sx={{ color: '#ccc', padding: '20px' }} variant='outlined' size='large' onClick={() => history.replace(`/system`)}>
                                Voltar
                            </Button>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
            <ImagePreviewModal
                fileOrUrl={fileOrUrl}
                open={modalOpen}
                onClose={() => setModalOpen(false)}
            />
        </>
    );
};
